/**
 * GuestSavePrompt — «Сохранить разговор, начатый до входа?» (SCRUM-274; spec
 * "Claim and return"). ChatRoute shows it in place of the transcript and the
 * composer while a member has an unclaimed guest conversation in this
 * browser and no fresh claim intent — a product choice for shared devices:
 * the person who just signed in may not be the guest.
 *
 * The preview line holds ONLY what the pointer holds — message count and last
 * activity — never the guest's text (C3 ruling: no preview endpoint).
 * Phases: "idle" → nothing yet (the controller decides within a commit, and
 * «Сохраняем…» there would announce a claim that may never start);
 * "claiming" → a quiet status line; "prompt" → the question; "retry" → the
 * question + the failure line (503, network, or a claim given up after
 * CLAIM_SETTLE_TIMEOUT_MS — B-3), where «Сохранить» is the retry.
 *
 * DRAFTED STATE (GUARDRAILS rule 10): no prototype counterpart. GuestGateCard's
 * slip surface centred in the chat column, the title in the display face (a
 * consumer title — DESIGN.md), shadcn Button at size lg (44px). Focus lands
 * on «Сохранить» when the question appears, so a keyboard user lands on the
 * decision; when «Сохранить» starts the claim and the buttons give way to the
 * status line, focus moves onto that line instead of dropping to <body>. The
 * failure line carries a glyph and plain ink on a tinted ground, the chat's
 * error-slot pattern — never semantic colour as small text. The house
 * fade-and-rise, nothing under reduced motion; the spinner is the
 * reduced-motion-aware `g-spin`.
 */
import { motion } from "framer-motion";
import { LoaderCircle, OctagonAlert } from "lucide-react";
import { useEffect, useId, useRef, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  useGuestSavePromptPresence,
  type GuestClaimPhase,
} from "@/features/chat/guest/GuestClaimController";
import type { GuestPointer } from "@/features/chat/guest/pointer";
import { relativeStamp } from "@/features/chat/ThreadListItem";
import { iconProps } from "@/lib/icons";
import { bubbleMotion } from "@/lib/motion";
import { useGardenMotion } from "@/lib/use-garden-motion";

export interface GuestSavePromptProps {
  phase: GuestClaimPhase;
  pointer: GuestPointer | null;
  onSave: () => void;
  onDontSave: () => void;
}

export function GuestSavePrompt({
  phase,
  pointer,
  onSave,
  onDontSave,
}: GuestSavePromptProps): ReactElement {
  const { t, i18n } = useTranslation("chat");
  const { reduced } = useGardenMotion();
  // While this prompt is mounted, its failure line below is the ONE report
  // of a failed claim — the controller sends no toast (final web fix round,
  // item 8).
  useGuestSavePromptPresence();
  const titleId = useId();
  const saveRef = useRef<HTMLButtonElement | null>(null);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const asking = phase === "prompt" || phase === "retry";
  // Whether the question was on screen at the previous commit: only a
  // question → status transition moves focus to the status line (the
  // focused button just unmounted); a cold "claiming" takes nothing.
  const wasAskingRef = useRef(false);

  useEffect(() => {
    if (asking) saveRef.current?.focus();
    else if (wasAskingRef.current) statusRef.current?.focus({ preventScroll: true });
    wasAskingRef.current = asking;
  }, [asking]);

  if (phase === "idle") {
    return <div className="flex flex-1" data-testid="chat-guest-claim-idle" />;
  }

  if (!asking) {
    return (
      <div
        ref={statusRef}
        // -1: a landing spot for focus, not a Tab stop.
        tabIndex={-1}
        className="flex flex-1 items-center justify-center px-4 focus:outline-none"
        data-testid="chat-guest-claiming"
      >
        <p role="status" className="flex items-center gap-2 text-[15px] text-[var(--g-ink-2)]">
          <LoaderCircle {...iconProps("md")} className="g-spin" />
          {t("guest.claim.saving")}
        </p>
      </div>
    );
  }

  const count = pointer?.allowance?.messages_used ?? 0;
  const when =
    pointer !== null ? relativeStamp(new Date(pointer.updatedAt).toISOString(), i18n.language) : "";
  const preview =
    count > 0
      ? t("guest.claim.promptPreview", { count, when })
      : t("guest.claim.promptPreviewNoCount", { when });

  return (
    <div className="flex flex-1 items-center justify-center px-4 py-6">
      <motion.section
        aria-labelledby={titleId}
        data-testid="chat-guest-save-prompt"
        data-phase={phase}
        className="flex w-full max-w-[520px] flex-col gap-3 rounded-[var(--g-radius-lg)] border border-[var(--g-rule)] bg-[var(--g-surface)] p-5"
        {...bubbleMotion(!reduced)}
      >
        <h2
          id={titleId}
          className="font-display text-[18px] font-bold leading-snug tracking-[-0.02em] text-foreground"
        >
          {t("guest.claim.promptTitle")}
        </h2>
        <p className="text-[14.5px] leading-[1.5] text-[var(--g-ink-2)]" data-testid="chat-guest-save-preview">
          {preview}
        </p>
        {phase === "retry" ? (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-[var(--g-radius)] border border-[color-mix(in_srgb,var(--g-error)_30%,transparent)] bg-[color-mix(in_srgb,var(--g-error)_7%,transparent)] px-3 py-2 text-[14.5px] leading-snug text-foreground"
            data-testid="chat-guest-save-failed"
          >
            <span className="mt-px shrink-0 text-[var(--g-error)]">
              <OctagonAlert {...iconProps("sm")} />
            </span>
            {t("guest.claim.failed")}
          </p>
        ) : null}
        <div className="mt-1 flex flex-wrap gap-2">
          <Button ref={saveRef} type="button" size="lg" onClick={onSave} data-testid="chat-guest-save">
            {t("guest.claim.save")}
          </Button>
          <Button
            type="button"
            size="lg"
            variant="outline"
            onClick={onDontSave}
            data-testid="chat-guest-dont-save"
          >
            {t("guest.claim.dontSave")}
          </Button>
        </div>
      </motion.section>
    </div>
  );
}

export default GuestSavePrompt;
